import React, { useState, useRef, useEffect, Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { usePermissions } from 'react-admin';
import { TableV3 } from '@/components/TableV3';
import LoadingV2 from '@/components/Loading';
import { PaginationV2 } from '@/components/PaginationV2';
import { WarningsModal } from '@/components/WarningStatusTerminal/WarningsModal';
import {
  adminAddMember,
  adminDeleteMember,
  adminEditMember,
  adminLoginToUserAccount,
  SaveData,
  SaveApiResMessage,
  verifyTwoStepMembers,
  adminChangeExport,
  adminChangeStreetTurn,
  adminChangeApoointment,
  adminChangeSchedules,
  adminEnableAutoBooks,
  adminChangeEmptyTracking,
  adminChangeLoadInventory,
  adminChangeTerminalCredentials,
  updateUSerSubscription,
  updateTrialDays,
  updatePrice,
  updateCredit,
  generateMemberBilling,
  getAdminWarnings,
} from '@/actions';
import DeleteMemberPopup from '@/components/DeletePopup';
import Filter from '@/components/Members/Filter';
import { validateAdminAddMemberData } from '@/actions/validations';
import { PASSCODE_SESSION_DURATION, PASSCODE_SESSION_KEY } from '@/constants';
import MemberActionMenu from './components/MemberActionMenu';
import TableActions from './components/TableActions';
import FilterBadges from './components/FilterBadges';
import SearchAndFilterBar from './components/SearchAndFilterBar';
import UpdateSubscriptionConfirmModal from './components/UpdateSubscriptionConfirmModal';
import { useMembersTableHeaders } from './hooks/useMembersTableHeaders';

const PasscodeModal = lazy(() => import('./components/PasscodeModal'));
const AccessLevelModal = lazy(() => import('./components/AccessLevelModal'));
const EditFieldModal = lazy(() => import('./components/EditFieldModal'));
const AddEditMemberModal = lazy(() =>
  import('./components/AddEditMemberModal')
);

const MembersList = ({
  DATA = [],
  notify,
  getMembers,
  filter,
  setFilter,
  setFilterForm,
  loading = false,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
}) => {
  const menuRef = useRef(null);
  const buttonRefs = useRef({});
  // Track which members have pending subscription field changes (plan, trial_days, or credit)
  const [membersWithChanges, setMembersWithChanges] = useState(new Set());
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    password: '',
  });
  const [loginLoading, setLoginLoading] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [options, setOptions] = React.useState({
    open: false,
    DATA: [],
  });
  const [userAvatar, setUserAvatar] = useState({
    url: null,
    image: null,
  });
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [editFieldModal, setEditFieldModal] = useState({
    open: false,
    member: null,
    field: null, // 'plan', 'trial_days', or 'credit'
    value: null,
  });
  const [passcodeModal, setPasscodeModal] = useState({
    open: false,
    memberId: null,
    field: null, // 'plan', 'trial_days', or 'credit'
    value: null,
  });
  const [passcodeLoading, setPasscodeLoading] = useState(false);
  const [fieldUpdateLoading, setFieldUpdateLoading] = useState(false);
  const [accessLevelModal, setAccessLevelModal] = useState({
    open: false,
    member: null,
  });
  const [updateSubscriptionModal, setUpdateSubscriptionModal] = useState({
    open: false,
    userId: null,
    memberName: '',
  });
  const [updateSubscriptionLoading, setUpdateSubscriptionLoading] =
    useState(false);
  const [warningsModal, setWarningsModal] = useState({
    show: false,
    data: [],
    memberId: null,
  });
  const [warningsLoadingMemberId, setWarningsLoadingMemberId] = useState(null);

  const { permissions } = usePermissions();
  const isAdmin = permissions === 'admin';

  const editpopUp = useSelector(
    (state) => state.reducer.editMemberPopup || null
  );
  const addpopUp = useSelector((state) => state.reducer.addMemberPopup || null);
  const deletepopUp = useSelector(
    (state) => state.reducer.deleteMemberPopup || null
  );

  const history = useHistory();

  const isPasscodeSessionValid = React.useCallback(() => {
    const sessionData = localStorage.getItem(PASSCODE_SESSION_KEY);
    if (!sessionData) return false;

    try {
      const { timestamp } = JSON.parse(sessionData);
      const now = Date.now();
      const elapsed = now - timestamp;
      return elapsed < PASSCODE_SESSION_DURATION;
    } catch (error) {
      return false;
    }
  }, []);

  const storePasscodeSession = React.useCallback(() => {
    const sessionData = {
      timestamp: Date.now(),
    };
    localStorage.setItem(PASSCODE_SESSION_KEY, JSON.stringify(sessionData));
  }, []);

  const handleOpenActionMenu = React.useCallback(
    (event, member) => {
      if (!actionMenuAnchor) {
        event.stopPropagation();
        const buttonElement = event.currentTarget;
        const rect = buttonElement.getBoundingClientRect();

        buttonRefs.current[member.id] = buttonElement;

        setMenuPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.right + window.scrollX - 140,
        });
        setActionMenuAnchor(buttonElement);
        setSelectedMember(member);
      } else {
        setActionMenuAnchor(null);
      }
    },
    [actionMenuAnchor]
  );

  const handleCloseActionMenu = React.useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedMember(null);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        actionMenuAnchor &&
        !actionMenuAnchor.contains(event.target)
      ) {
        handleCloseActionMenu();
      }
    };

    if (actionMenuAnchor) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [actionMenuAnchor, handleCloseActionMenu]);

  useEffect(() => {
    const handleScroll = () => {
      if (actionMenuAnchor) {
        handleCloseActionMenu();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [actionMenuAnchor, handleCloseActionMenu]);

  const handleEditMember = React.useCallback(() => {
    if (selectedMember) {
      SaveData({ editMemberPopup: selectedMember });
    }
    handleCloseActionMenu();
  }, [selectedMember, handleCloseActionMenu]);

  const handleDeleteMemberFromMenu = React.useCallback(() => {
    if (selectedMember) {
      SaveData({ deleteMemberPopup: selectedMember.id });
    }
    handleCloseActionMenu();
  }, [selectedMember, handleCloseActionMenu]);

  const handlePaymentHistory = React.useCallback(
    async (user_id) => {
      if (!selectedMember && !user_id) {
        handleCloseActionMenu();
        return;
      }

      try {
        const response = await generateMemberBilling({
          user_id: user_id || selectedMember.id,
        });

        const url = response.data.url;

        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          SaveApiResMessage({
            title: 'Error',
            message: 'No URL found in response',
            mode: 'error',
          });
        }
      } catch (error) {
        SaveApiResMessage({
          title: 'Error',
          message: error,
          mode: 'error',
        });
      } finally {
        handleCloseActionMenu();
      }
    },
    [selectedMember, handleCloseActionMenu]
  );

  const handleFieldClick = React.useCallback((member, field) => {
    setEditFieldModal({
      open: true,
      member,
      field,
      value: null,
    });
  }, []);

  const handleEditFieldClose = React.useCallback(() => {
    setEditFieldModal({ open: false, member: null, field: null, value: null });
    setPasscodeModal({ open: false, memberId: null, field: null, value: null });
    setPasscodeLoading(false);
    setFieldUpdateLoading(false);
  }, []);

  const handleDirectFieldUpdate = React.useCallback(
    (memberId, field, value) => {
      setFieldUpdateLoading(true);

      if (field === 'plan') {
        updatePrice({ user_id: memberId, price: value })
          .then((res) => {
            SaveApiResMessage({
              title: res?.messageHeader,
              message: res?.message || 'successfully updated',
              mode: 'success',
            });
            handleEditFieldClose();
            // Mark this member as having changes
            setMembersWithChanges((prev) => new Set(prev).add(memberId));
            getMembers();
          })
          .catch((err) => {
            SaveApiResMessage({
              title: 'Error',
              message: err,
              mode: 'warning',
            });
          })
          .finally(() => {
            setFieldUpdateLoading(false);
          });
      } else if (field === 'trial_days') {
        updateTrialDays({ user_id: memberId, trial_duration: value })
          .then((res) => {
            SaveApiResMessage({
              title: res?.data?.messageHeader,
              message: res?.message || 'successfully updated',
              mode: 'success',
            });
            handleEditFieldClose();
            // Mark this member as having changes
            setMembersWithChanges((prev) => new Set(prev).add(memberId));
            getMembers();
          })
          .catch((err) => {
            SaveApiResMessage({
              title: 'Error',
              message: err,
              mode: 'warning',
            });
          })
          .finally(() => {
            setFieldUpdateLoading(false);
          });
      } else if (field === 'credit') {
        updateCredit({ user_id: memberId, credit: value })
          .then((res) => {
            SaveApiResMessage({
              title: res?.messageHeader,
              message: res?.message || 'successfully updated',
              mode: 'success',
            });
            handleEditFieldClose();
            // Mark this member as having changes
            setMembersWithChanges((prev) => new Set(prev).add(memberId));
            getMembers();
          })
          .catch((err) => {
            SaveApiResMessage({
              title: 'Error',
              message: err,
              mode: 'warning',
            });
          })
          .finally(() => {
            setFieldUpdateLoading(false);
          });
      }
    },
    [getMembers, handleEditFieldClose]
  );

  const handleEditFieldSave = React.useCallback(
    (memberId, field, value) => {
      if (isPasscodeSessionValid()) {
        handleDirectFieldUpdate(memberId, field, value);
      } else {
        setPasscodeModal({
          open: true,
          memberId,
          field,
          value,
        });
      }
    },
    [isPasscodeSessionValid, handleDirectFieldUpdate]
  );

  const handlePasscodeSubmit = React.useCallback(
    async (passcode) => {
      setPasscodeLoading(true);

      try {
        const response = await verifyTwoStepMembers(passcode);

        if (response.message !== 'success') {
          setPasscodeLoading(false);
          SaveApiResMessage({
            title: 'Error',
            message: response.message,
            mode: 'warning',
          });
          return; // Don't proceed if passcode verification fails
        }

        const { memberId, field, value } = passcodeModal;

        handleDirectFieldUpdate(memberId, field, value);
        storePasscodeSession();
      } catch (error) {
        setPasscodeLoading(false);
        SaveApiResMessage({
          title: 'Error',
          message: 'Passcode verification failed',
          mode: 'warning',
        });
      }
    },
    [passcodeModal, handleDirectFieldUpdate, storePasscodeSession]
  );

  const handlePasscodeClose = React.useCallback(() => {
    setPasscodeModal({ open: false, memberId: null, field: null, value: null });
    setPasscodeLoading(false);
  }, []);

  const handleCloseAccessLevelModal = React.useCallback(() => {
    setAccessLevelModal({ open: false, member: null });
  }, []);

  const handleUpdateUserSubscriptionClick = React.useCallback(
    (user_id, memberName) => {
      setUpdateSubscriptionModal({
        open: true,
        userId: user_id,
        memberName: memberName || 'this member',
      });
    },
    []
  );

  const handleUpdateUserSubscriptionConfirm = React.useCallback(() => {
    const { userId } = updateSubscriptionModal;
    if (!userId) return;

    setUpdateSubscriptionLoading(true);
    updateUSerSubscription({ user_id: userId })
      .then((res) => {
        SaveApiResMessage({
          title: res?.data?.messageHeader,
          message: 'successfully updated',
        });
        // Clear the change tracking for this member after successful update
        setMembersWithChanges((prev) => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
        getMembers();
        setUpdateSubscriptionModal({
          open: false,
          userId: null,
          memberName: '',
        });
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
      })
      .finally(() => {
        setUpdateSubscriptionLoading(false);
      });
  }, [updateSubscriptionModal, getMembers]);

  const handleCloseUpdateSubscriptionModal = React.useCallback(() => {
    setUpdateSubscriptionModal({ open: false, userId: null, memberName: '' });
    setUpdateSubscriptionLoading(false);
  }, []);

  const handleOpenWarningsModal = React.useCallback(async (member) => {
    setWarningsLoadingMemberId(member.id);
    try {
      const response = await getAdminWarnings(member.id);

      let warningData = [];
      if (response?.data?.warnings) {
        const warnings = response.data.warnings;

        warningData = Object.values(warnings).map((warning) => {
          return {
            ...warning,
          };
        });
      }

      setWarningsModal({
        show: true,
        data: warningData,
        memberId: member.id,
      });
    } catch (error) {
      console.error('Error fetching admin warnings:', error);
      // Fallback to empty data or show error message
      setWarningsModal({
        show: true,
        data: [],
        memberId: member.id,
      });
    } finally {
      setWarningsLoadingMemberId(null);
    }
  }, []);

  const handleCloseWarningsModal = React.useCallback(() => {
    setWarningsModal({ show: false, data: [], memberId: null });
    setWarningsLoadingMemberId(null);
  }, []);

  const handleRefetchWarningsData = React.useCallback(async () => {
    if (!warningsModal.memberId) return;

    try {
      const response = await getAdminWarnings(warningsModal.memberId);

      let warningData = [];
      if (response?.data?.warnings) {
        const warnings = response.data.warnings;

        warningData = Object.values(warnings).map((warning) => {
          return {
            ...warning,
          };
        });
      }

      setWarningsModal((prev) => ({
        ...prev,
        data: warningData,
      }));
    } catch (error) {
      console.error('Error refetching admin warnings:', error);
    }
  }, [warningsModal.memberId]);

  const renderTableActions = React.useCallback(
    (member) => {
      const hasChanges = membersWithChanges.has(member.id);
      return (
        <TableActions
          member={member}
          hasChanges={hasChanges}
          onUpdateSubscription={(userId) =>
            handleUpdateUserSubscriptionClick(
              userId,
              `${member.name} ${member.last_name || ''}`
            )
          }
          onOpenSettings={(member) =>
            setAccessLevelModal({ open: true, member })
          }
          onOpenActionMenu={handleOpenActionMenu}
          handlePaymentHistory={handlePaymentHistory}
        />
      );
    },
    [
      handleOpenActionMenu,
      handleUpdateUserSubscriptionClick,
      membersWithChanges,
      handlePaymentHistory,
    ]
  );

  const handleChangeStatus = React.useCallback(
    (id, status) => {
      adminEditMember({ status, id, isEditStatus: true })
        .then((res) => {
          SaveApiResMessage({
            title: res?.data?.messageHeader,
            message: 'successfully updated',
            mode: 'success',
          });
          getMembers();
        })
        .catch((err) => {
          SaveApiResMessage({
            title: 'Error',
            message: err,
            mode: 'warning',
          });
        });
    },
    [getMembers]
  );

  const handleDeleteMember = (member) => {
    adminDeleteMember(member).then((res) => {
      if (res.status === 'success') {
        getMembers();
        SaveApiResMessage({
          title: res?.data?.messageHeader,
          message: 'member successfully deleted',
          mode: 'success',
        });
      }
    });
    SaveData({ deleteMemberPopup: false });
  };

  const handleLoginUser = React.useCallback(
    (user_id) => {
      setLoginLoading(user_id);
      const adminToken = localStorage.getItem('token');
      adminLoginToUserAccount(user_id)
        .then((data) => {
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('permissions', 'user');
          localStorage.setItem('token_admin', adminToken);
          history.push('/inventory');
          window.location.reload();

          SaveApiResMessage({
            title: data?.messageHeader || 'Success',
            message: 'Redirect to User Account',
            mode: 'success',
          });
          setLoginLoading(null);
        })
        .catch((err) => {
          SaveApiResMessage({
            title: 'Error',
            message: err,
            mode: 'warning',
          });
          setLoginLoading(null);
        });
    },
    [history]
  );

  const updateMembers = (data) => {
    adminEditMember(data)
      .then((res) => {
        SaveData({ editMemberPopup: false });
        SaveApiResMessage({
          title: res?.data?.messageHeader,
          message: 'successfully updated',
          mode: 'success',
        });
        getMembers();
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
      });
  };

  const handleSaveMember = (data) => {
    if (editpopUp) {
      updateMembers(data);
    } else {
      handleAddMember(data);
    }
  };

  const handleAddMember = (data) => {
    // Use data from modal if provided, otherwise use formData
    let memberData = data ? { ...data } : { ...formData };

    // If data doesn't have image but userAvatar does, add it
    if (!memberData.image && userAvatar.image) {
      memberData.image = userAvatar.image;
    }

    let error = validateAdminAddMemberData(memberData);
    if (error) {
      SaveApiResMessage({
        title: 'Warning',
        message: error,
        mode: 'warning',
      });
    } else {
      adminAddMember(memberData)
        .then((res) => {
          if (res.status === 'success') {
            SaveApiResMessage({
              title: res?.data?.messageHeader,
              message: res.message,
              mode: 'success',
            });
            getMembers();
            resetAddMemberValues();
            SaveData({ addMemberPopup: false });
          }
        })
        .catch((res) => {
          if (res.errors) {
            Object.keys(res.errors).forEach((message) => {
              SaveApiResMessage({
                title: 'Error',
                message: res.errors[message][0],
                mode: 'warning',
              });
            });
          } else {
            SaveApiResMessage({
              title: 'Error',
              message: res,
              mode: 'warning',
            });
          }
        });
    }
  };

  const handleCloseMemberModal = () => {
    if (editpopUp) {
      SaveData({ editMemberPopup: false });

      resetAddMemberValues();
    } else {
      SaveData({ addMemberPopup: false });
      resetAddMemberValues();
    }
  };

  const resetAddMemberValues = () => {
    setFormData({
      id: '',
      name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      password: '',
    });
    setUserAvatar({
      image: '',
      url: '',
    });
  };

  const headers = useMembersTableHeaders({
    loginLoading,
    handleLoginUser,
    handleChangeStatus,
    handleFieldClick,
    renderTableActions,
    handleOpenWarningsModal,
    warningsLoadingMemberId,
  });

  return (
    <>
      <SearchAndFilterBar
        filter={filter}
        setFilter={setFilter}
        setOptions={setOptions}
      />

      <Filter
        title="Members"
        filter={filter}
        setFilter={setFilter}
        setFilterForm={setFilterForm}
        DATA={DATA}
        notify={notify}
      />

      <FilterBadges
        filter={filter}
        setFilter={setFilter}
        setFilterForm={setFilterForm}
      />

      {loading ? (
        <div className="flex justify-center py-4">
          <LoadingV2 />
        </div>
      ) : (
        <div id="members-table-wrapper" className="mb-4">
          <TableV3
            headers={headers}
            data={DATA || []}
            emptyMessage="No members found"
            minWidth="1600px"
            stickyHeader={true}
            appHeaderHeight={64}
          />
          {totalPages > 1 && (
            <div className="mt-4">
              <PaginationV2
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
              />
            </div>
          )}
        </div>
      )}

      {actionMenuAnchor && (
        <MemberActionMenu
          menuRef={menuRef}
          menuPosition={menuPosition}
          onEdit={handleEditMember}
          onDelete={handleDeleteMemberFromMenu}
          onPaymentHistory={handlePaymentHistory}
        />
      )}
      <Suspense fallback={null}>
        <AddEditMemberModal
          show={!!editpopUp || !!addpopUp}
          onClose={handleCloseMemberModal}
          member={editpopUp}
          onSave={handleSaveMember}
          formData={formData}
          setFormData={setFormData}
          userAvatar={userAvatar}
          setUserAvatar={setUserAvatar}
        />
      </Suspense>
      <DeleteMemberPopup
        show={!!deletepopUp}
        handleDelete={() => handleDeleteMember(deletepopUp)}
        closePopup={() => SaveData({ deleteMemberPopup: false })}
      />

      <UpdateSubscriptionConfirmModal
        show={updateSubscriptionModal.open}
        memberName={updateSubscriptionModal.memberName}
        handleConfirm={handleUpdateUserSubscriptionConfirm}
        closePopup={handleCloseUpdateSubscriptionModal}
        loading={updateSubscriptionLoading}
      />

      <Suspense fallback={null}>
        <EditFieldModal
          show={editFieldModal.open}
          onClose={handleEditFieldClose}
          member={editFieldModal.member}
          field={editFieldModal.field}
          onSave={handleEditFieldSave}
          loading={fieldUpdateLoading}
        />
      </Suspense>

      <Suspense fallback={null}>
        <PasscodeModal
          show={passcodeModal.open}
          onClose={handlePasscodeClose}
          onSubmit={handlePasscodeSubmit}
          loading={passcodeLoading}
        />
      </Suspense>

      <Suspense fallback={null}>
        <AccessLevelModal
          show={accessLevelModal.open}
          onClose={handleCloseAccessLevelModal}
          member={accessLevelModal.member}
          apiFunctions={{
            adminChangeTerminalCredentials,
            adminChangeSchedules,
            adminChangeLoadInventory,
            adminChangeEmptyTracking,
            adminChangeExport,
            adminChangeStreetTurn,
            adminChangeApoointment,
            adminEnableAutoBooks,
          }}
          getMembers={getMembers}
        />
      </Suspense>

      <WarningsModal
        show={warningsModal.show}
        setShow={handleCloseWarningsModal}
        data={warningsModal.data}
        isAdmin={isAdmin}
        onRefetchData={() => {
          handleRefetchWarningsData();
          getMembers();
        }}
      />
    </>
  );
};

export default MembersList;
